import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Check,
  ChevronRight,
  Home as HomeIcon,
  ShieldCheck,
  AlertTriangle,
  Star,
  MapPin,
  Waves,
} from "lucide-react";
import { COMPANY } from "../lib/constants";
import useSeo, { breadcrumbJsonLd, SITE } from "../lib/useSeo";

const TOC = [
  { id: "enjeux", t: "Pourquoi la baie du MSM est-elle vulnérable ?" },
  { id: "risques", t: "Les 5 risques spécifiques d'une résidence secondaire" },
  { id: "checklist", t: "Checklist sécurité avant de quitter le bien" },
  { id: "equipements", t: "Les équipements qui font la différence" },
  { id: "conciergerie", t: "Pourquoi passer par une conciergerie ?" },
  { id: "budget", t: "Budget & aides fiscales" },
];

const TOWNS = [
  { slug: "mont-saint-michel", name: "Le Mont-Saint-Michel" },
  { slug: "pontorson", name: "Pontorson" },
  { slug: "cherrueix", name: "Cherrueix" },
  { slug: "saint-coulomb", name: "Saint-Coulomb" },
  { slug: "cancale", name: "Cancale" },
  { slug: "saint-suliac", name: "Saint-Suliac" },
  { slug: "hirel", name: "Hirel" },
  { slug: "le-vivier-sur-mer", name: "Le Vivier-sur-Mer" },
  { slug: "saint-marcan", name: "Saint-Marcan" },
];

export default function BlogResidenceSecondaireBaieMSM() {
  useSeo({
    title:
      "Sécuriser sa résidence secondaire dans la baie du Mont-Saint-Michel — Guide 2026 | Mon Serrurier Breton",
    description:
      "Le guide complet pour protéger votre résidence secondaire dans la baie du Mont-Saint-Michel : risques, équipements, checklist avant départ, budget et solutions locales. Conseils d'un artisan serrurier basé à Mont-Dol.",
    path: "/blog/securiser-residence-secondaire-baie-mont-saint-michel",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Accueil", path: "/" },
        { name: "Blog", path: "/blog/securiser-residence-secondaire-baie-mont-saint-michel" },
        {
          name: "Sécuriser sa résidence secondaire baie MSM",
          path: "/blog/securiser-residence-secondaire-baie-mont-saint-michel",
        },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline:
          "Comment sécuriser sa résidence secondaire dans la baie du Mont-Saint-Michel ?",
        description:
          "Guide complet 2026 : risques spécifiques, équipements, checklist départ, budget et pièges à éviter pour protéger votre résidence secondaire baie MSM.",
        author: { "@type": "Organization", name: SITE.name, url: SITE.url },
        publisher: {
          "@type": "Organization",
          name: SITE.name,
          logo: { "@type": "ImageObject", url: `${SITE.url}/logo.png` },
        },
        datePublished: "2026-08-03",
        dateModified: "2026-08-03",
        image: `${SITE.url}/og-image.jpg`,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${SITE.url}/blog/securiser-residence-secondaire-baie-mont-saint-michel`,
        },
        about: [
          { "@type": "Place", name: "Baie du Mont-Saint-Michel" },
          { "@type": "Place", name: "Le Mont-Saint-Michel" },
          { "@type": "Thing", name: "Résidence secondaire" },
          { "@type": "Service", name: "Serrurerie et sécurité" },
        ],
      },
    ],
  });

  return (
    <article data-testid="page-blog-residence-secondaire">
      <nav
        className="max-w-5xl mx-auto px-4 md:px-8 pt-8 text-xs text-slate-500 flex items-center gap-2 flex-wrap"
        aria-label="Fil d'Ariane"
      >
        <Link to="/" className="inline-flex items-center gap-1 hover:text-teal-700">
          <HomeIcon className="w-3.5 h-3.5" /> Accueil
        </Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-slate-900 font-semibold">
          Sécuriser une résidence secondaire — baie MSM
        </span>
      </nav>

      <header className="max-w-5xl mx-auto px-4 md:px-8 pt-10 pb-10">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-4">
          <Waves className="w-4 h-4" /> Guide propriétaires · 10 min de lecture
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
          Comment sécuriser sa{" "}
          <span className="text-teal-600">résidence secondaire</span> dans la baie du Mont-Saint-Michel ?
        </h1>
        <p className="text-slate-600 mt-6 text-lg leading-relaxed max-w-3xl">
          Vous possédez une longère à Cherrueix, une maison de pêcheur à Saint-Coulomb, un cottage à Saint-Marcan ou un appartement à Pontorson ? Ce guide vous explique tout ce que vous devez savoir pour protéger votre bien pendant vos absences — de la porte d'entrée au coffre, en passant par les partenaires locaux de confiance.
        </p>
        <div className="mt-6 flex items-center gap-4 text-sm text-slate-500">
          <span>Publié le 3 août 2026</span>
          <span>·</span>
          <span>Par Mon Serrurier Breton — Mont-Dol</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 order-2 lg:order-1">
            <div className="lg:sticky lg:top-28 bg-slate-50 border border-slate-200 rounded-3xl p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
                Sommaire
              </div>
              <ol className="space-y-2 text-sm">
                {TOC.map((t, i) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      className="flex items-start gap-3 text-slate-700 hover:text-teal-700 transition-colors"
                    >
                      <span className="font-display font-black text-slate-300 w-6 flex-shrink-0">
                        0{i + 1}
                      </span>
                      <span>{t.t}</span>
                    </a>
                  </li>
                ))}
              </ol>
              <div className="mt-6 pt-5 border-t border-slate-200">
                <div className="text-xs font-bold uppercase tracking-widest text-orange-600 mb-2">
                  Besoin d'un audit sécurité ?
                </div>
                <a
                  href={`tel:${COMPANY.phoneDigits}`}
                  data-testid="blog2-aside-call"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-5 py-3 shadow-lg shadow-orange-600/30 text-sm w-full justify-center"
                >
                  <Phone className="w-4 h-4" /> {COMPANY.phone}
                </a>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8 order-1 lg:order-2 space-y-12 text-slate-700 leading-relaxed">
            <section id="enjeux" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                01 · Pourquoi la baie du Mont-Saint-Michel est-elle particulièrement vulnérable ?
              </h2>
              <p className="mb-4">
                La baie du Mont-Saint-Michel concentre une densité exceptionnelle de résidences secondaires. Entre <Link to="/serrurier-cherrueix" className="text-teal-700 font-semibold hover:underline">Cherrueix</Link>, <Link to="/serrurier-le-vivier-sur-mer" className="text-teal-700 font-semibold hover:underline">Le Vivier-sur-Mer</Link>, <Link to="/serrurier-hirel" className="text-teal-700 font-semibold hover:underline">Hirel</Link>, <Link to="/serrurier-saint-coulomb" className="text-teal-700 font-semibold hover:underline">Saint-Coulomb</Link> et <Link to="/serrurier-cancale" className="text-teal-700 font-semibold hover:underline">Cancale</Link>, on estime qu'une maison sur trois est utilisée moins de 6 mois par an.
              </p>
              <p className="mb-4">
                Cette forte proportion crée un profil de risque particulier : <strong>de longues périodes de vacance</strong>, une visibilité du bien (voitures absentes, volets fermés, courrier accumulé), et une <strong>saison touristique où les allées et venues sont massives</strong>, rendant les repérages difficiles à détecter. Résultat : les cambriolages ciblés se concentrent d'octobre à mars, période où les propriétaires sont le moins présents.
              </p>
              <p>
                À cela s'ajoute un climat marin très corrosif qui dégrade les serrures et cylindres non protégés en quelques années — un point souvent négligé qui rend les fermetures plus faciles à forcer avec le temps.
              </p>
            </section>

            <section id="risques" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                02 · Les 5 risques spécifiques
              </h2>
              <div className="space-y-4">
                {[
                  { t: "Cambriolage saisonnier", d: "Concentrés hors saison, ciblés grâce au repérage des propriétaires réguliers. Priorité aux résidences visibles depuis la voie publique." },
                  { t: "Squat de résidence vide", d: "Rare mais en hausse dans les zones touristiques. Une fois installés, les squatteurs sont difficiles à déloger juridiquement (jusqu'à 6-12 mois de procédure)." },
                  { t: "Effraction sans vol par curiosité", d: "Certaines résidences sont visitées par des personnes qui repartent sans rien voler — souvent pour préparer un futur cambriolage. À traiter comme un cambriolage complet." },
                  { t: "Dégât des eaux non détecté", d: "Une porte qui claque en votre absence, un cambriolage, du vent violent → intrusion d'humidité → sinistre majeur découvert des mois plus tard." },
                  { t: "Vol par prestataires malhonnêtes", d: "Femmes de ménage, jardiniers, artisans mal identifiés à qui on confie des clés. Reproduire une clé standard coûte 3€ chez n'importe quel serrurier." },
                ].map((c, i) => (
                  <div key={c.t} className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-2xl p-5">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white font-display font-extrabold flex items-center justify-center flex-shrink-0 text-sm">
                      0{i + 1}
                    </div>
                    <div>
                      <div className="font-display font-bold text-slate-900 mb-1">{c.t}</div>
                      <div className="text-sm text-slate-600">{c.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="checklist" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                03 · Checklist avant de quitter votre résidence
              </h2>
              <div className="bg-teal-50 border border-teal-200 rounded-3xl p-6 md:p-8">
                <ul className="space-y-3">
                  {[
                    "Vérifier que toutes les serrures multipoints sont en position \"3 tours\" (relevage complet)",
                    "Contrôler les fermetures des volets, portes de garage et cave",
                    "Débrancher les appareils électriques non-essentiels (limite les incendies)",
                    "Faire relever le courrier par un voisin ou souscrire à la garde par La Poste",
                    "Programmer un simulateur de présence (variations lumière + radio)",
                    "Prévenir un voisin ou une conciergerie locale de votre absence",
                    "Photographier l'intérieur du bien (utile en cas de sinistre)",
                    "Fermer l'arrivée d'eau générale + couper le gaz",
                    "Activer la détection intrusion si équipé (alarme, caméra)",
                    "Ne rien annoncer publiquement sur les réseaux sociaux",
                  ].map((p, i) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-teal-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-slate-800 leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section id="equipements" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                04 · Les équipements qui font vraiment la différence
              </h2>
              <p className="mb-4">
                Pour une résidence secondaire en baie du MSM, voici l'ordre de priorité que nous recommandons à nos clients depuis Mont-Dol :
              </p>
              <div className="space-y-4 mb-4">
                {[
                  { t: "Cylindre A2P 3 étoiles", d: "L'investissement #1 (150-250€ posé). Résiste 15 minutes minimum à l'effraction — largement au-delà du temps où un voleur abandonne. Marques recommandées : Bricard, Vachette, Héraclès, Picard.", prix: "150-250€" },
                  { t: "Serrure multipoints A2P", d: "Si vous n'en avez pas encore, priorité absolue. Trois ou cinq points d'ancrage rendent la porte quasi-impossible à forcer sans destruction visible.", prix: "500-1500€" },
                  { t: "Blindage de porte", d: "Pour les portes anciennes qu'on souhaite conserver esthétiquement. Une plaque acier fixée sur l'existant + un cadre renforcé.", prix: "800-1800€" },
                  { t: "Alarme connectée avec caméra", d: "Le duo dissuasion + preuve. Applications smartphone avec alertes en temps réel + enregistrement cloud sécurisé.", prix: "200-800€ + abonnement" },
                  { t: "Détecteur d'ouverture volets + fenêtres", d: "Points d'entrée souvent oubliés. Petits capteurs connectés à l'alarme pour couvrir toutes les ouvertures.", prix: "20-40€/capteur" },
                ].map((e, i) => (
                  <div key={e.t} className="bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="font-display font-bold text-slate-900">
                        {i + 1}. {e.t}
                      </div>
                      <span className="text-xs bg-teal-100 text-teal-700 font-bold px-2 py-1 rounded-full whitespace-nowrap">
                        {e.prix}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed">{e.d}</div>
                  </div>
                ))}
              </div>
            </section>

            <section id="conciergerie" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                05 · Pourquoi passer par une conciergerie locale ?
              </h2>
              <p className="mb-4">
                Confier vos clés à une <strong>conciergerie professionnelle locale</strong> — comme Les Clés Bretonnes ou Ma Conciergerie Bretonne, nos partenaires — c'est déléguer la vigilance à des professionnels de confiance qui :
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  "Passent régulièrement vérifier l'état du bien",
                  "Réceptionnent le courrier et les colis",
                  "Aèrent les pièces et vérifient l'absence de dégât des eaux",
                  "Sont contact unique pour les artisans en cas d'intervention",
                  "Fournissent un rapport photo mensuel de leur passage",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <p>
                Ces conciergeries sont aussi souvent les <strong>meilleurs relais serrurerie</strong> : elles sauront immédiatement qui appeler en cas de problème (nous, par exemple), et coordonneront l'intervention en votre absence.
              </p>
            </section>

            <section id="budget" className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                06 · Budget & aides fiscales
              </h2>
              <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-2xl p-5 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-700">
                    <strong>Bon à savoir :</strong> les travaux de sécurisation d'une résidence secondaire ne bénéficient pas de MaPrimeRénov'. En revanche, votre assurance habitation peut réduire votre prime de 5 à 15% après équipement A2P.
                  </div>
                </div>
              </div>
              <p className="mb-4">
                <strong>Budget indicatif pour un pack complet</strong> (cylindre A2P + serrure 3 points + alarme connectée + détecteurs) : entre <strong>1 500 et 3 500€ TTC</strong>. Investissement rentabilisé en 3-5 ans par la seule réduction d'assurance et surtout par la sérénité gagnée.
              </p>
              <p>
                Nous établissons un <strong>devis gratuit sur site</strong> pour toute résidence secondaire dans un rayon de 30 km autour de Mont-Dol. Facture claire, tarifs annoncés d'avance, garantie sur les pièces et la main-d'œuvre.
              </p>
            </section>

            <section className="scroll-mt-32">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-4">
                <MapPin className="w-6 h-6 inline mr-2 text-teal-600" />
                Nos interventions par commune baie MSM
              </h2>
              <p className="mb-4 text-slate-600">
                Nous intervenons pour toute urgence ou audit sécurité de résidence secondaire sur les communes suivantes de la baie du Mont-Saint-Michel :
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {TOWNS.map((t) => (
                  <Link
                    key={t.slug}
                    to={`/serrurier-${t.slug}`}
                    className="group bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-teal-500 hover:shadow-md transition-all"
                  >
                    <div className="font-display font-bold text-slate-900 text-sm group-hover:text-teal-700 leading-tight">
                      Serrurier {t.name}
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">
                      Fiche dédiée
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            <section className="scroll-mt-32">
              <div className="rounded-3xl bg-slate-900 text-white p-8 md:p-10 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-amber-400/15 blur-3xl" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-300 mb-3">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    Audit sécurité gratuit
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-extrabold mb-3 leading-tight">
                    Faites auditer votre résidence secondaire
                    <br />
                    <span className="text-amber-300">avant votre prochain départ.</span>
                  </h3>
                  <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl">
                    30 minutes sur place · analyse fermetures + points faibles + devis chiffré · aucun engagement · gratuit dans un rayon de 30 km autour de Mont-Dol.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`tel:${COMPANY.phoneDigits}`}
                      className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-6 py-3.5 shadow-lg shadow-orange-600/30"
                    >
                      <Phone className="w-4 h-4" /> {COMPANY.phone}
                    </a>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold rounded-full px-6 py-3.5"
                    >
                      Demander un audit <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </article>
  );
}
